/**
 * Review a job-change suggestion (SC-13586): "Jane may have moved to Globex — what should we do?"
 *
 * Three steps, all in one dialog:
 *   choose   current vs new side by side (old AND new email shown explicitly), and only the
 *            choices valid for this contact — Keep both (pre-selected, the one choice that
 *            changes nothing about the old company), Link to the existing contact, Move, or
 *            Only update the title. Plus "Not now" (personal dismiss).
 *   confirm  states exactly what will happen, with live counts from contactChangeImpact.
 *   done     what was written, with a link to the record that now matters.
 * Any non-pending suggestion — including one a colleague resolved while this was open
 * (ALREADY_RESOLVED) — renders the resolved state instead: who did what, and when.
 *
 * Nothing here is ever deleted, and accept recomputes server-side against the live records.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, Check, Loader2, TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEntitySheetParams } from "@/lib/hooks/use-entity-sheet-params";
import {
  useContactChangeActions,
  useContactChangeImpact,
  useContactChangeSuggestion,
  type AcceptMode,
  type ContactChangeSuggestion,
  type SuggestionActionResult,
} from "@/lib/hooks/use-contact-change-suggestion";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useFormatters } from "@/lib/i18n/useFormatters";
import { cn } from "@/lib/utils";

type Step = "choose" | "confirm" | "done";

/** i18n keys spelled out (locales.test.ts only counts quoted literals as used). */
const MODE_KEYS: Record<AcceptMode, { label: string; help: string; confirmTitle: string; confirmCta: string }> = {
  CREATE_NEW: {
    label: "detailSheets.jobChange.mode.CREATE_NEW.label",
    help: "detailSheets.jobChange.mode.CREATE_NEW.help",
    confirmTitle: "detailSheets.jobChange.mode.CREATE_NEW.confirmTitle",
    confirmCta: "detailSheets.jobChange.mode.CREATE_NEW.confirmCta",
  },
  LINK_EXISTING: {
    label: "detailSheets.jobChange.mode.LINK_EXISTING.label",
    help: "detailSheets.jobChange.mode.LINK_EXISTING.help",
    confirmTitle: "detailSheets.jobChange.mode.LINK_EXISTING.confirmTitle",
    confirmCta: "detailSheets.jobChange.mode.LINK_EXISTING.confirmCta",
  },
  MOVE: {
    label: "detailSheets.jobChange.mode.MOVE.label",
    help: "detailSheets.jobChange.mode.MOVE.help",
    confirmTitle: "detailSheets.jobChange.mode.MOVE.confirmTitle",
    confirmCta: "detailSheets.jobChange.mode.MOVE.confirmCta",
  },
  TITLE_ONLY: {
    label: "detailSheets.jobChange.mode.TITLE_ONLY.label",
    help: "detailSheets.jobChange.mode.TITLE_ONLY.help",
    confirmTitle: "detailSheets.jobChange.mode.TITLE_ONLY.confirmTitle",
    confirmCta: "detailSheets.jobChange.mode.TITLE_ONLY.confirmCta",
  },
};

const RESOLVED_KEYS: Record<AcceptMode, string> = {
  CREATE_NEW: "detailSheets.jobChange.resolved.CREATE_NEW",
  LINK_EXISTING: "detailSheets.jobChange.resolved.LINK_EXISTING",
  MOVE: "detailSheets.jobChange.resolved.MOVE",
  TITLE_ONLY: "detailSheets.jobChange.resolved.TITLE_ONLY",
};

/** Which choices to offer, given what exists at the new email. Pure — exported for tests. */
export function offeredModes(s: ContactChangeSuggestion, existingContactId: string | null): AcceptMode[] {
  if (s.kind === "TITLE_CHANGED") return ["TITLE_ONLY"];
  // A live contact already at the new email: creating or moving would leave two contacts on
  // one address, so offer linking instead.
  const order: AcceptMode[] = existingContactId
    ? ["LINK_EXISTING", "TITLE_ONLY"]
    : ["CREATE_NEW", "MOVE", "TITLE_ONLY"];
  return order.filter(m => s.availableModes.includes(m));
}

interface Props {
  suggestionId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after any successful accept or dismiss, so the banner can refetch. */
  onResolved?: () => void;
}

export default function JobChangeReviewDialog({ suggestionId, open, onOpenChange, onResolved }: Props) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const { currentUser: user } = useCurrentUser();
  const { openEntitySheet } = useEntitySheetParams();

  const { suggestion: fetched, loading } = useContactChangeSuggestion(suggestionId, open);
  const [override, setOverride] = useState<ContactChangeSuggestion | null>(null);
  const suggestion = override ?? fetched;

  const [step, setStep] = useState<Step>("choose");
  const [mode, setMode] = useState<AcceptMode | null>(null);
  const [createCompany, setCreateCompany] = useState(true);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SuggestionActionResult | null>(null);

  const isCompany = suggestion?.kind === "COMPANY_CHANGED";
  // Needed to decide which choices exist, so fetched as soon as a company question is open.
  const { impact, loading: impactLoading } = useContactChangeImpact(suggestionId, open && isCompany);
  const { accept, dismiss, reopen, busy } = useContactChangeActions();

  const modes = useMemo(
    () => (suggestion ? offeredModes(suggestion, impact?.existingContactId ?? null) : []),
    [suggestion, impact?.existingContactId],
  );

  // Reset on open, and pre-select the first offered choice (Keep both, when offered).
  useEffect(() => {
    if (!open) return;
    setStep("choose");
    setOverride(null);
    setError(null);
    setResult(null);
    setCreateCompany(true);
    setAccountId(null);
  }, [open, suggestionId]);
  useEffect(() => {
    if (modes.length && (!mode || !modes.includes(mode))) setMode(modes[0]);
  }, [modes, mode]);

  const snap = suggestion?.snapshot ?? {};
  const vars = {
    personName: snap.personName ?? t("detailSheets.jobChange.thisContact"),
    fromCompany: snap.fromCompany ?? "—",
    toCompany: snap.toCompany ?? "—",
    fromEmail: snap.fromEmail ?? "—",
    toEmail: snap.toEmail ?? "—",
    fromTitle: snap.fromTitle ?? "—",
    toTitle: snap.toTitle ?? "—",
    domain: snap.toCompanyDomain ?? "",
  };
  const needsCompanyChoice = isCompany && (mode === "CREATE_NEW" || mode === "MOVE");

  const run = async (fn: () => Promise<SuggestionActionResult>) => {
    setError(null);
    try {
      const r = await fn();
      if (r.suggestion) setOverride(r.suggestion);
      if (r.ok) {
        setResult(r);
        onResolved?.();
        return r;
      }
      if (r.errorCode === "ALREADY_RESOLVED") return r;           // re-renders as resolved
      if (r.errorCode === "EXISTING_CONTACT_AT_NEW_EMAIL") {
        setStep("choose");
        setError(t("detailSheets.jobChange.error.existingContact", { toEmail: vars.toEmail }));
        return r;
      }
      setError(r.errorMessage ?? t("detailSheets.jobChange.error.generic"));
      return r;
    } catch {
      setError(t("detailSheets.jobChange.error.generic"));
      return null;
    }
  };

  const onConfirm = async () => {
    if (!suggestion || !mode) return;
    const r = await run(() => accept({
      id: suggestion.id, mode,
      createCompany: impact?.accountMatch === "none" ? createCompany : true,
      accountId: impact?.accountMatch === "ambiguous" ? accountId : null,
    }));
    if (r?.ok) setStep("done");
  };

  const onDismiss = async () => {
    if (!suggestion) return;
    const r = await run(() => dismiss(suggestion.id));
    if (r?.ok) onOpenChange(false);
  };

  const onUndoDismiss = async () => {
    if (!suggestion) return;
    setError(null);
    try {
      const r = await reopen(suggestion.id);
      if (r.suggestion) setOverride(r.suggestion);
      if (r.ok) {
        setStep("choose");
        onResolved?.();
      } else if (r.errorCode === "SUPERSEDED") {
        setError(t("detailSheets.jobChange.error.superseded"));
      } else {
        setError(r.errorMessage ?? t("detailSheets.jobChange.error.generic"));
      }
    } catch {
      setError(t("detailSheets.jobChange.error.generic"));
    }
  };

  const openContact = (id: string | null | undefined) => {
    if (!id) return;
    onOpenChange(false);
    openEntitySheet("personSheet", id);
  };

  // ---------------------------------------------------------------------------------------------

  const body = (() => {
    if (loading && !suggestion) {
      return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
    }
    if (!suggestion) {
      return <p className="text-sm text-muted-foreground py-6">{t("detailSheets.jobChange.notFound")}</p>;
    }

    if (step === "done" && result) {
      return (
        <div className="space-y-4 py-2">
          <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 p-4">
            <Check className="h-5 w-5 text-success mt-0.5 shrink-0" />
            <p className="text-sm">
              {t(RESOLVED_KEYS[mode as AcceptMode], {
                ...vars, actor: t("detailSheets.jobChange.you"), date: formatDate(new Date().toISOString()),
              })}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>{t("detailSheets.jobChange.close")}</Button>
            <Button onClick={() => openContact(result.resolvedContactId)}>{t("detailSheets.jobChange.openContact")}</Button>
          </DialogFooter>
        </div>
      );
    }

    // The viewer said "Not now". Their dismissal is theirs to undo while nobody has accepted
    // it — whether it is still open for colleagues (pending) or closed because every
    // recipient dismissed it.
    if (suggestion.dismissedByViewer && (suggestion.status === "pending" || suggestion.status === "dismissed")) {
      return (
        <div className="space-y-4 py-2">
          <p className="text-sm">{t("detailSheets.jobChange.dismissedByYou")}</p>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t("detailSheets.jobChange.close")}</Button>
            <Button onClick={onUndoDismiss} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("detailSheets.jobChange.undo")}
            </Button>
          </DialogFooter>
        </div>
      );
    }

    if (suggestion.status !== "pending") {
      const byViewer = user?.id != null && String(suggestion.actedByUserId) === String(user.id);
      const resolvedKey = suggestion.acceptMode ? RESOLVED_KEYS[suggestion.acceptMode] : null;
      return (
        <div className="space-y-4 py-2">
          <p className="text-sm">
            {suggestion.status === "accepted" && resolvedKey
              ? t(resolvedKey, {
                  ...vars,
                  actor: byViewer ? t("detailSheets.jobChange.you") : suggestion.actedByName ?? t("detailSheets.jobChange.someone"),
                  date: suggestion.actedTs ? formatDate(suggestion.actedTs) : "",
                })
              : t("detailSheets.jobChange.noLongerOpen")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>{t("detailSheets.jobChange.close")}</Button>
            {suggestion.resolvedContactId && (
              <Button onClick={() => openContact(suggestion.resolvedContactId)}>{t("detailSheets.jobChange.openContact")}</Button>
            )}
          </DialogFooter>
        </div>
      );
    }

    if (step === "confirm" && mode) {
      return (
        <div className="space-y-4 py-2">
          <ul className="space-y-2 text-sm list-disc pl-5">
            {mode === "CREATE_NEW" && <li>{t("detailSheets.jobChange.confirm.createNew", vars)}</li>}
            {mode === "CREATE_NEW" && <li>{t("detailSheets.jobChange.confirm.oldUntouched", vars)}</li>}
            {mode === "LINK_EXISTING" && <li>{t("detailSheets.jobChange.confirm.linkExisting", vars)}</li>}
            {mode === "LINK_EXISTING" && <li>{t("detailSheets.jobChange.confirm.oldUntouched", vars)}</li>}
            {mode === "MOVE" && <li>{t("detailSheets.jobChange.confirm.moveEmail", vars)}</li>}
            {mode === "MOVE" && <li>{t("detailSheets.jobChange.confirm.moveCompany", vars)}</li>}
            {mode === "MOVE" && impact?.engagementCount != null && (
              <li>{t("detailSheets.jobChange.confirm.history", { ...vars, count: impact.engagementCount })}</li>
            )}
            {mode === "TITLE_ONLY" && <li>{t("detailSheets.jobChange.confirm.titleOnly", vars)}</li>}
            {mode !== "LINK_EXISTING" && mode !== "TITLE_ONLY" && impact?.accountMatch === "none" && createCompany && (
              <li>{t("detailSheets.jobChange.confirm.createCompany", vars)}</li>
            )}
            <li>{t("detailSheets.jobChange.confirm.asYou")}</li>
          </ul>
          {mode === "MOVE" && (impact?.dealCount ?? 0) > 0 && (
            <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
              <TriangleAlert className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <span>{t("detailSheets.jobChange.confirm.deals", { ...vars, count: impact?.dealCount ?? 0, names: (impact?.dealNames ?? []).join(", ") })}</span>
            </div>
          )}
          {(mode === "MOVE" || mode === "TITLE_ONLY") && (impact?.syncedTo.length ?? 0) > 0 && (
            <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/5 p-3 text-sm">
              <TriangleAlert className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <span>{t("detailSheets.jobChange.confirm.synced")}</span>
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep("choose")} disabled={busy}>{t("detailSheets.jobChange.back")}</Button>
            <Button onClick={onConfirm} disabled={busy}>
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t(MODE_KEYS[mode].confirmCta, vars)}
            </Button>
          </DialogFooter>
        </div>
      );
    }

    // choose
    return (
      <div className="space-y-4 py-2">
        <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2 text-sm">
          <Side heading={t("detailSheets.jobChange.current")} company={snap.fromCompany} email={isCompany ? snap.fromEmail : null}
                title={snap.fromTitle} start={snap.fromStartDate} formatDate={formatDate} />
          <ArrowRight className="h-4 w-4 text-muted-foreground self-center" />
          <Side heading={t("detailSheets.jobChange.suggested")} company={snap.toCompany} email={isCompany ? snap.toEmail : null}
                title={snap.toTitle} start={snap.toStartDate} formatDate={formatDate} highlight />
        </div>

        {isCompany && impactLoading && !impact ? (
          <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
        ) : (
          <RadioGroup value={mode ?? undefined} onValueChange={v => setMode(v as AcceptMode)} className="space-y-2">
            {modes.map(m => (
              <label key={m} htmlFor={`jc-${m}`}
                className={cn("flex gap-3 rounded-lg border p-3 cursor-pointer transition",
                  mode === m ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40")}>
                <RadioGroupItem id={`jc-${m}`} value={m} className="mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium">{t(MODE_KEYS[m].label, { ...vars, existingName: impact?.existingContactName ?? vars.personName })}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t(MODE_KEYS[m].help, vars)}</p>
                </div>
              </label>
            ))}
          </RadioGroup>
        )}

        {needsCompanyChoice && impact?.accountMatch === "none" && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={createCompany} onCheckedChange={v => setCreateCompany(v === true)} />
            <Building2 className="h-4 w-4 text-muted-foreground" />
            {t("detailSheets.jobChange.createCompany", vars)}
          </label>
        )}
        {needsCompanyChoice && impact?.accountMatch === "ambiguous" && (
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t("detailSheets.jobChange.pickCompany", vars)}</p>
            <Select value={accountId ?? undefined} onValueChange={setAccountId}>
              <SelectTrigger><SelectValue placeholder={t("detailSheets.jobChange.pickCompanyPlaceholder")} /></SelectTrigger>
              <SelectContent>
                {impact.accountCandidates.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name ?? c.domain ?? c.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={onDismiss} disabled={busy}>{t("detailSheets.jobChange.notNow")}</Button>
          <Button
            onClick={() => setStep("confirm")}
            disabled={!mode || busy || (needsCompanyChoice && impact?.accountMatch === "ambiguous" && !accountId)
              || (needsCompanyChoice && impact?.accountMatch === "none" && !createCompany)}
          >
            {t("detailSheets.jobChange.continue")}
          </Button>
        </DialogFooter>
      </div>
    );
  })();

  const titleKey = !suggestion
    ? "detailSheets.jobChange.titleGeneric"
    : isCompany ? "detailSheets.jobChange.titleCompany" : "detailSheets.jobChange.titleTitle";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {step === "confirm" && mode ? t(MODE_KEYS[mode].confirmTitle, vars) : t(titleKey, vars)}
          </DialogTitle>
          <DialogDescription>
            {step === "confirm" ? t("detailSheets.jobChange.confirmDescription") : t("detailSheets.jobChange.description")}
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

function Side(props: {
  heading: string;
  company?: string | null;
  email?: string | null;
  title?: string | null;
  start?: string | null;
  highlight?: boolean;
  formatDate: (d: string) => string;
}) {
  const { heading, company, email, title, start, highlight, formatDate } = props;
  return (
    <div className={cn("rounded-lg border p-3 space-y-0.5 min-w-0", highlight ? "border-primary/40 bg-primary/5" : "border-border")}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{heading}</p>
      <p className="font-medium truncate">{company ?? "—"}</p>
      {title && <p className="text-muted-foreground truncate">{title}</p>}
      {email && <p className="text-muted-foreground truncate">{email}</p>}
      {start && <p className="text-xs text-muted-foreground">{start.length > 7 ? formatDate(start) : start}</p>}
    </div>
  );
}
